import { useEffect, useRef } from 'react';

/** Decorative digit reels. The accessible value is always the exact final value. */
export default function ReferralAnimatedNumber({ value, format = 'number', animate = true }) {
  const root = useRef(null);
  const number = Number(value);
  const safeValue = Number.isFinite(number) ? number : 0;
  const digits = safeValue.toLocaleString(undefined, {
    minimumFractionDigits: format === 'number' ? 0 : 2,
    maximumFractionDigits: format === 'number' ? 0 : 2,
  });
  const label = format === 'cash' ? `$${digits}` : format === 'fp' ? `${digits} FP` : digits;

  useEffect(() => {
    const element = root.current;
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let animations = [];
    let observer;
    let disposed = false;
    const finish = () => {
      animations.forEach(animation => animation.cancel());
      animations = [];
      if (element) element.dataset.animating = 'false';
    };
    const play = () => {
      observer?.disconnect();
      if (!element || !animate || safeValue === 0 || preference?.matches || document.hidden || !element.animate) return;
      element.dataset.animating = 'true';
      animations = [...element.querySelectorAll('[data-reel]')].map(reel => reel.animate([
        { transform: 'translateY(0)' },
        { transform: reel.style.transform },
      ], { duration: 800, easing: 'cubic-bezier(.16, 1, .3, 1)' }));
      Promise.all(animations.map(animation => animation.finished.catch(() => {})))
        .then(() => { if (!disposed) finish(); });
    };
    const onPreference = () => { if (preference?.matches) { observer?.disconnect(); finish(); } };
    const onVisibility = () => { if (document.hidden) { observer?.disconnect(); finish(); } };
    if (window.IntersectionObserver) {
      observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) play(); }, { threshold: .2 });
      observer.observe(element);
    } else { play(); }
    preference?.addEventListener?.('change', onPreference);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      observer?.disconnect();
      finish();
      preference?.removeEventListener?.('change', onPreference);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [label, animate, safeValue]);

  return <span className="referral-number" ref={root} data-value={safeValue} data-format={format}>
    <span className="referral-number-readable">{label}</span>
    <span className="referral-number-visual" aria-hidden="true">
      {[...label].map((character, index) => /[0-9]/.test(character)
        ? <span className="referral-number-window" key={index}>
          <span className="referral-number-reel" data-reel style={{ transform: `translateY(-${20 + Number(character)}em)` }}>
            {Array.from({ length: 21 + Number(character) }, (_, step) => <span className="referral-number-cell" data-character={step % 10} key={step} />)}
          </span>
        </span>
        : <span className="referral-number-character" data-character={character} key={index} />)}
    </span>
  </span>;
}
